import type { HanziText, PinyinText } from "@/data/model";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import { Pressable, ScrollView } from "react-native";
import { Text } from "@/client/ui/Text";
import { View } from "@/client/ui/View";
import { Icon } from "./Icon";
import { PylyMdxComponents } from "./PylyMdxComponents";
import { useDb } from "./hooks/useDb";
import { Theme } from "./Theme";

export const NewSkillModalContentNewPronunciation = ({
  hanzi,
  onDismiss,
}: {
  hanzi: HanziText;
  onDismiss: () => void;
}) => {
  const db = useDb();
  const { data: dictionaryEntries } = useLiveQuery({
    query: (q) =>
      q
        .from({ entry: db.dictionaryCollection })
        .where(({ entry }) => eq(entry.hanzi, hanzi))
        .orderBy(({ entry }) => entry.hskSortKey, `asc`)
        .orderBy(({ entry }) => entry.hanziWord, `asc`)
        .select(({ entry }) => ({
          gloss: entry.gloss,
          pinyin: entry.pinyin,
        })),
  });

  let pinyin: PinyinText | undefined;
  for (const entry of dictionaryEntries) {
    if (entry.pinyin != null) {
      const mainPinyin = entry.pinyin[0];
      if (mainPinyin != null) {
        pinyin = mainPinyin;
        break;
      }
    }
  }

  let title: string = hanzi;
  if (pinyin != null) {
    title += ` (${pinyin})`;
  }

  const glosses =
    dictionaryEntries.length === 1
      ? dictionaryEntries[0]?.gloss.join(`, `)
      : dictionaryEntries.map((entry) => entry.gloss[0]).join(`, `);

  return (
    <ScrollView
      className={
        // Use a linear gradient on the background so that rubber band
        // scrolling showing the correct color at the top and bottom.
        `
          h-screen
          bg-[linear-gradient(to_bottom,_var(--color-theme-grass-panel-bg)_0%,_var(--color-theme-grass-panel-bg)_50%,_var(--color-bg)_50%,_var(--color-bg)_100%)]
        `
      }
      contentContainerClassName="min-h-full pb-10"
    >
      <Header title={title} subtitle={glosses} onDismiss={onDismiss} />

      <PylyMdxComponents>
        <View className="flex-1 gap-2 bg-bg py-7">
          <View className="flex-row items-center gap-2 px-4">
            <Icon
              icon="voice-square"
              size={32}
              className="text-[var(--color-theme-grass-panel-bg)]"
            />
            <Text className="pyly-body-title text-fg-loud">Pronunciation</Text>
          </View>
        </View>
      </PylyMdxComponents>
    </ScrollView>
  );
};

function Header({
  title,
  subtitle,
  onDismiss,
}: {
  title: string;
  subtitle?: string;
  onDismiss?: () => void;
}) {
  const [ref, entry] = useIntersectionObserver();

  return (
    <>
      {/* Scroll detector */}
      <View
        className="absolute top-15 h-0 w-full"
        ref={(el) => {
          ref(el as Element | null);
        }}
      />

      <Theme theme="grass-panel">
        <View className="sticky -top-30 z-10 h-46 bg-bg">
          <View className="sticky top-1 z-10 h-14 flex-row items-center pl-4">
            <Pressable
              onPress={onDismiss}
              className={`
                size-8 rounded-md transition-transform

                hover:bg-fg-loud/10

                active:scale-95
              `}
            >
              <Icon
                icon="close"
                size={32}
                tintColorClassName="accent-fg-loud"
              />
            </Pressable>
          </View>

          <View className="mb-2 self-center rounded-md bg-fg-loud/10 px-2 py-1">
            <Text
              className={`
                text-center font-sans text-[12px]/[14px] font-bold text-fg-loud uppercase
                transition-all
              `}
            >
              New pronunciation
            </Text>
          </View>

          <View className="sticky top-[11px] z-0 overflow-visible px-4">
            <Text
              className={`
                text-center font-sans text-[28px]/[42px] font-bold text-fg-loud transition-all

                ${entry?.isIntersecting === false ? `scale-75` : `scale-100`}
              `}
            >
              {title}
            </Text>
          </View>

          <View className="">
            <Text
              className={`
                text-center font-sans text-[18px] font-normal text-fg-loud transition-opacity

                ${entry?.isIntersecting === false ? `opacity-0` : `opacity-100`}
              `}
            >
              {subtitle}
            </Text>
          </View>
        </View>
      </Theme>
    </>
  );
}
